import {
  Box,
  Button,
  Chip,
  Paper,
  Typography,
  useTheme
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faComments,
  faArrowRight
} from "@fortawesome/free-solid-svg-icons";

import { formatDecimal } from "@aclymatepackages/formatters";
import { hexToRgba } from "@aclymatepackages/converters";

import Link from "../../../atoms/mui/Link";
import ScoutEmissionsChart from "../../scout/cards/ScoutEmissionsChart";
import { resolveDisplayTotalTons } from "../../views/dashboardHelpers";

const TALK_TO_EXPERT_URL =
  "https://meetings.hubspot.com/aclymate/meeting-with-aclymate";

const REDUCTION_OPPORTUNITIES_URL = "/explorer/upgrade";

const FACILITIES_SUBCATEGORIES = new Set([
  "electricity",
  "gas",
  "refrigerants",
  "chill-water",
  "steam",
  "purchased-heat",
  "purchased-water"
]);

const CARD_SX = {
  p: 2,
  borderRadius: "8px",
  flex: "1 1 320px",
  minWidth: 320,
  display: "flex",
  flexDirection: "column",
  gap: 1
};

const findScopeTons = (scopePieSeries, scope) => {
  const entry = (scopePieSeries || []).find(
    ({ label }) => label === `Scope ${scope}`
  );
  return Number(entry?.value || 0);
};

const computeShare = (value, total) => {
  if (!total) {
    return 0;
  }
  return (value / total) * 100;
};

const sumFacilitiesEmissions = (surveyEmissions) =>
  (surveyEmissions || [])
    .filter(({ subcategory }) => FACILITIES_SUBCATEGORIES.has(subcategory))
    .reduce((sum, { tonsCo2e }) => sum + (Number(tonsCo2e) || 0), 0);

const buildOfficeBreakdown = (offices, facilitiesTons) => {
  const safeOffices = (offices || []).filter((office) => office?.name);
  if (!safeOffices.length || facilitiesTons <= 0) {
    return [];
  }
  const totalSqFootage = safeOffices.reduce(
    (sum, { squareFootage }) => sum + (Number(squareFootage) || 0),
    0
  );
  const useSqFootage = totalSqFootage > 0;
  return safeOffices
    .map((office) => {
      const ratio = useSqFootage
        ? (Number(office.squareFootage) || 0) / totalSqFootage
        : 1 / safeOffices.length;
      const tons = facilitiesTons * ratio;
      return { name: office.name, tons, share: ratio * 100 };
    })
    .sort((a, b) => b.tons - a.tons);
};

const ScopeCard = ({ label, tons, totalTons, helperText, showPercent }) => {
  const { palette } = useTheme();
  const share = computeShare(tons, totalTons);

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        borderRadius: "8px",
        flex: "1 1 200px",
        minWidth: 180,
        display: "flex",
        flexDirection: "column",
        gap: 0.5
      }}
    >
      <Typography variant="subtitle2" color="textPrimary">
        {label}
      </Typography>
      <Box display="flex" alignItems="baseline" gap={0.75}>
        <Typography variant="h4" color="textPrimary">
          {formatDecimal(tons)}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          tCO₂e
        </Typography>
      </Box>
      <Typography variant="caption" color="textSecondary">
        {showPercent
          ? `${formatDecimal(share)}% of footprint`
          : helperText || ""}
      </Typography>
      <Box mt="auto" pt={0.5}>
        <Chip
          label="Low confidence"
          size="small"
          sx={{
            bgcolor: palette.backgroundGray.main,
            color: palette.text.secondary
          }}
        />
      </Box>
    </Paper>
  );
};

const ProportionBar = ({ label, share, tons }) => {
  const { palette } = useTheme();
  const width = Math.max(0, Math.min(100, Math.round(share || 0)));
  return (
    <Box mb={1.5}>
      <Box display="flex" justifyContent="space-between" mb={0.5} gap={1}>
        <Typography variant="body2" color="textPrimary" noWrap>
          {label}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {tons === undefined
            ? `${formatDecimal(share || 0)}%`
            : `${formatDecimal(tons)} tCO₂e (${formatDecimal(share || 0)}%)`}
        </Typography>
      </Box>
      <Box
        sx={{
          height: 8,
          borderRadius: "4px",
          backgroundColor: palette.backgroundGray.main
        }}
      >
        <Box
          sx={{
            height: 8,
            borderRadius: "4px",
            width: `${width}%`,
            backgroundColor: palette.secondary.main
          }}
        />
      </Box>
    </Box>
  );
};

const HotspotsCard = ({ hotspots }) => {
  const hasHotspots = (hotspots || []).length > 0;
  return (
    <Paper elevation={1} sx={CARD_SX}>
      <Typography variant="subtitle1" color="textPrimary">
        C. Top emission hotspots
      </Typography>
      <Box flexGrow={1}>
        {!hasHotspots && (
          <Typography variant="body2" color="textSecondary">
            No hotspots yet — complete your estimate to see them.
          </Typography>
        )}
        {(hotspots || []).map((hotspot) => (
          <ProportionBar
            key={hotspot.categoryKey || hotspot.label}
            label={hotspot.label}
            share={hotspot.share}
          />
        ))}
      </Box>
    </Paper>
  );
};

const LocationsCard = ({ offices, facilitiesTons }) => {
  const officeBreakdown = buildOfficeBreakdown(offices, facilitiesTons);
  const noOffices = !officeBreakdown.length;

  return (
    <Paper elevation={1} sx={CARD_SX}>
      <Typography variant="subtitle1" color="textPrimary">
        D. Emissions by location
      </Typography>
      <Box flexGrow={1}>
        {noOffices && (
          <Typography variant="body2" color="textSecondary">
            Add a facility in your estimate to see emissions by location.
          </Typography>
        )}
        {officeBreakdown.map((office) => (
          <ProportionBar
            key={office.name}
            label={office.name}
            share={office.share}
            tons={office.tons}
          />
        ))}
        {!noOffices && (
          <Typography variant="caption" color="textSecondary">
            Facilities emissions split by square footage across reporting
            offices.
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const ReadyBanner = ({ year, surveyStatus }) => {
  const { palette } = useTheme();
  const effectiveStatus = year && surveyStatus ? surveyStatus : "none";
  const bannerCopyMap = {
    complete: {
      headline: `Your ${year} footprint estimate is ready 🎉`,
      subline: "Explore the breakdown below or download the report to share."
    },
    "initial-estimate-ready": {
      headline: `Your ${year} initial estimate is ready`,
      subline:
        "Complete the remaining survey sections to finalize your footprint."
    },
    partial: {
      headline: `Your ${year} estimate is in progress`,
      subline: "Complete the estimate to unlock the full breakdown."
    },
    none: {
      headline: "Start your footprint estimate",
      subline:
        "Kick off your survey to see your emissions breakdown here."
    }
  };
  const { headline, subline } =
    bannerCopyMap[effectiveStatus] || bannerCopyMap.none;
  return (
    <Box
      p={2}
      sx={{
        borderRadius: "8px",
        bgcolor: hexToRgba(palette.secondary.light, 0.18),
        border: `1px solid ${hexToRgba(palette.secondary.main, 0.4)}`
      }}
    >
      <Typography variant="subtitle1" color="textPrimary">
        {headline}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {subline}
      </Typography>
    </Box>
  );
};

const FooterActions = ({ pdfReportDownload, year, downloadDisabled }) => {
  const { palette } = useTheme();
  return (
    <Box
      display="flex"
      gap={2}
      flexWrap="wrap"
      alignItems="center"
      justifyContent="space-between"
    >
      <Box display="flex" gap={2} flexWrap="wrap">
        <Button
          variant="contained"
          color="secondary"
          disabled={downloadDisabled}
          startIcon={<FontAwesomeIcon icon={faDownload} />}
          onClick={() => pdfReportDownload.trigger({ year })}
          sx={{ color: palette.common.white }}
        >
          Download report
        </Button>
        <Link href={TALK_TO_EXPERT_URL} newTab>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<FontAwesomeIcon icon={faComments} />}
          >
            Talk to an expert
          </Button>
        </Link>
      </Box>
      <Link href={REDUCTION_OPPORTUNITIES_URL}>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Typography variant="body2" color="secondary">
            See reduction opportunities
          </Typography>
          <FontAwesomeIcon
            icon={faArrowRight}
            style={{ fontSize: 12, color: palette.secondary.main }}
          />
        </Box>
      </Link>
    </Box>
  );
};

const MyFootprint = ({
  year,
  totalTons,
  surveyEmissions,
  scopePieSeries,
  hotspots,
  offices,
  surveyStatus,
  pdfReportDownload
}) => {
  const surveyComplete = surveyStatus === "complete";
  const scope1Tons = findScopeTons(scopePieSeries, 1);
  const scope2Tons = findScopeTons(scopePieSeries, 2);
  const scope3Tons = findScopeTons(scopePieSeries, 3);
  const breakdownTotal = resolveDisplayTotalTons({ totalTons, surveyEmissions });
  const facilitiesTons = sumFacilitiesEmissions(surveyEmissions);
  const hasEmissions = (surveyEmissions || []).length > 0 && breakdownTotal > 0;

  return (
    <Box display="flex" flexDirection="column" gap={3} sx={{ minWidth: 0 }}>
      <Box>
        <Typography variant="h4" color="textPrimary">
          My Footprint
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Review your estimated emissions, major hotspots, and where your
          footprint comes from.
        </Typography>
      </Box>

      <ReadyBanner year={year} surveyStatus={surveyStatus} />

      <Box display="flex" gap={2} flexWrap="wrap" alignItems="stretch">
        <ScopeCard
          label="Total footprint"
          tons={breakdownTotal}
          totalTons={breakdownTotal}
          helperText="Total estimated footprint"
        />
        <ScopeCard
          label="Scope 1"
          tons={scope1Tons}
          totalTons={breakdownTotal}
          showPercent
        />
        <ScopeCard
          label="Scope 2"
          tons={scope2Tons}
          totalTons={breakdownTotal}
          showPercent
        />
        <ScopeCard
          label="Scope 3"
          tons={scope3Tons}
          totalTons={breakdownTotal}
          showPercent
        />
      </Box>

      {hasEmissions && (
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="stretch">
          <Box
            sx={{
              flex: "1 1 320px",
              minWidth: 320,
              display: "flex",
              "& > .MuiPaper-root": { maxWidth: "none", width: "100%" }
            }}
          >
            <ScoutEmissionsChart
              title="A. Emissions by scope"
              emissions={surveyEmissions}
              viewMode="scopes"
            />
          </Box>
          <Box
            sx={{
              flex: "1 1 320px",
              minWidth: 320,
              display: "flex",
              "& > .MuiPaper-root": { maxWidth: "none", width: "100%" }
            }}
          >
            <ScoutEmissionsChart
              title="B. Emissions by activity category"
              emissions={surveyEmissions}
              viewMode="subcategories"
            />
          </Box>
        </Box>
      )}

      <Box display="flex" gap={2} flexWrap="wrap" alignItems="stretch">
        <HotspotsCard hotspots={hotspots} />
        <LocationsCard offices={offices} facilitiesTons={facilitiesTons} />
      </Box>

      <FooterActions
        pdfReportDownload={pdfReportDownload}
        year={year}
        downloadDisabled={!surveyComplete}
      />
    </Box>
  );
};

export default MyFootprint;
